import type { InfiniteData, QueryClient } from '@tanstack/vue-query'

import { chatsQueryKeys } from './query-keys.utils'
import { MessageSignal, SpecialMessageId } from '../types/chats.enums'

import type { IConversation, IConversationsResponse, IMessage } from '../types/conversation.types'

/**
 * Chat cache helpers for TanStack Query.
 * These utilities update cached chat data (messages/conversations) without refetching,
 * which is useful for socket events and optimistic UI updates.
 */
export interface MessagesPage {
  messages: IMessage[]
  hasMore: boolean
  nextCursor?: string
}

/**
 * Builds a predicate to match all conversation list queries for a location.
 * @param locationId - Location to scope the cache keys.
 */
export const getConversationsListPredicate = (locationId: string) => {
  return (query: { queryKey: readonly unknown[] }) => {
    const key = query.queryKey
    return Array.isArray(key) && key[0] === 'chats' && key[1] === locationId && key[2] === 'conversations' && key[3] === 'list'
  }
}

/**
 * Updates the first page of a messages infinite query using the provided updater.
 * @param queryClient - TanStack Query client instance.
 * @param key - Query key for the messages infinite query.
 * @param updater - Function that receives current messages and returns the updated list.
 */
export const updateMessagesCache = (queryClient: QueryClient, key: readonly unknown[], updater: (messages: IMessage[]) => IMessage[]) => {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(key, data => {
    if (!data?.pages?.length) {
      return data
    }

    const [firstPage, ...restPages] = data.pages
    const updatedFirstPage: MessagesPage = {
      ...firstPage,
      messages: updater(firstPage.messages || []),
    }

    return {
      ...data,
      pages: [updatedFirstPage, ...restPages],
    }
  })
}

/**
 * Updates a single-conversation cache entry by key.
 * @param queryClient - TanStack Query client instance.
 * @param key - Query key for the conversation.
 * @param updater - Function that receives current conversation and returns the updated value.
 */
export const updateConversationCache = (
  queryClient: QueryClient,
  key: readonly unknown[],
  updater: (conversation: IConversation) => IConversation
) => {
  queryClient.setQueryData<IConversation | undefined>(key, data => {
    if (!data) {
      return data
    }
    return updater(data)
  })
}

/**
 * Updates a conversation across all cached conversation lists for a location.
 * Returns true if the conversation was found in any cache.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all list caches.
 * @param conversationId - Conversation to update.
 * @param updater - Function that receives current conversation and returns the updated value.
 */
export const updateConversationListCaches = (
  queryClient: QueryClient,
  locationId: string,
  conversationId: string,
  updater: (conversation: IConversation) => IConversation
) => {
  let found = false
  queryClient.setQueriesData<InfiniteData<IConversationsResponse>>(
    {
      predicate: getConversationsListPredicate(locationId),
    },
    data => {
      if (!data?.pages?.length) {
        return data
      }

      const pages = data.pages.map(page => {
        const conversations = page.conversations.map(conversation => {
          if (conversation._id !== conversationId) {
            return conversation
          }

          found = true
          return updater(conversation)
        })

        return {
          ...page,
          conversations,
        }
      })

      return {
        ...data,
        pages,
      }
    }
  )

  return found
}

/**
 * Collects all cached conversation list queries for a location.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all list caches.
 */
const getConversationListQueries = (queryClient: QueryClient, locationId: string) => {
  return queryClient.getQueriesData<InfiniteData<IConversationsResponse>>({
    predicate: getConversationsListPredicate(locationId),
  })
}

/**
 * Inserts or updates a conversation in all list caches for a location.
 * Respects unread-filtered lists by skipping items with zero unread count.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all list caches.
 * @param conversation - Conversation to insert or update.
 */
export const upsertConversationInListCaches = (queryClient: QueryClient, locationId: string, conversation: IConversation) => {
  const entries = getConversationListQueries(queryClient, locationId)
  const unreadCount = conversation.unreadCount ?? 0

  entries.forEach(([key, data]) => {
    if (!data?.pages?.length) {
      return
    }

    const filter = Array.isArray(key) ? (key[4] as { unread?: boolean } | undefined) : undefined
    const isUnreadList = filter?.unread === true

    if (isUnreadList && unreadCount === 0) {
      return
    }

    let found = false
    const pages = data.pages.map(page => {
      const conversations = page.conversations.map(existing => {
        if (existing._id !== conversation._id) {
          return existing
        }
        found = true
        return {
          ...existing,
          ...conversation,
        }
      })

      return {
        ...page,
        conversations,
      }
    })

    if (!found) {
      const [firstPage, ...restPages] = pages
      const updatedFirstPage = {
        ...firstPage,
        conversations: [conversation, ...firstPage.conversations],
      }
      queryClient.setQueryData(key, {
        ...data,
        pages: [updatedFirstPage, ...restPages],
      })
      return
    }

    queryClient.setQueryData(key, {
      ...data,
      pages,
    })
  })
}

/**
 * Sets the single conversation cache AND upserts to all list caches in one call.
 * Use this when creating or fully updating a conversation to keep both caches in sync.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all caches.
 * @param conversation - Conversation to set/upsert.
 */
export const setConversationInAllCaches = (queryClient: QueryClient, locationId: string, conversation: IConversation) => {
  // Set single conversation cache
  const conversationKey = chatsQueryKeys.conversation(locationId, conversation._id)
  queryClient.setQueryData(conversationKey, conversation)

  // Upsert to all list caches
  upsertConversationInListCaches(queryClient, locationId, conversation)
}

/**
 * Updates a conversation in all caches (single conversation + all list caches) using an updater function.
 * Combines updateConversationCache + updateConversationListCaches into a single call.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all caches.
 * @param conversationId - Conversation to update.
 * @param updater - Function that receives the conversation and returns the updated version.
 * @returns true if the conversation was found in any list cache.
 */
export const updateConversationInAllCaches = (
  queryClient: QueryClient,
  locationId: string,
  conversationId: string,
  updater: (conversation: IConversation) => IConversation
): boolean => {
  // Update single conversation cache
  const conversationKey = chatsQueryKeys.conversation(locationId, conversationId)
  updateConversationCache(queryClient, conversationKey, updater)

  // Update in all list caches
  return updateConversationListCaches(queryClient, locationId, conversationId, updater)
}

/**
 * Updates conversation caches with a new message (used for both optimistic and socket updates).
 * Updates both the single conversation cache and all list caches.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all caches.
 * @param conversationId - Conversation to update.
 * @param message - New message to prepend to the conversation.
 */
export const updateConversationWithMessage = (queryClient: QueryClient, locationId: string, conversationId: string, message: IMessage) => {
  const updateFn = (conversation: IConversation) => ({
    ...conversation,
    messages: [message, ...removeTypingMessages(conversation.messages || [])],
    lastMessageAt: message.createdAt,
  })

  const conversationKey = chatsQueryKeys.conversation(locationId, conversationId)
  updateConversationCache(queryClient, conversationKey, updateFn)
  updateConversationListCaches(queryClient, locationId, conversationId, updateFn)
}

/**
 * Removes a conversation from all cached conversation lists for a location.
 * @param queryClient - TanStack Query client instance.
 * @param locationId - Location to scope all list caches.
 * @param conversationId - Conversation to remove.
 */
export const removeConversationFromListCaches = (queryClient: QueryClient, locationId: string, conversationId: string) => {
  const entries = getConversationListQueries(queryClient, locationId)

  entries.forEach(([key, data]) => {
    if (!data?.pages?.length) {
      return
    }

    const pages = data.pages.map(page => ({
      ...page,
      conversations: page.conversations.filter(conversation => conversation._id !== conversationId),
    }))

    queryClient.setQueryData(key, {
      ...data,
      pages,
    })
  })
}

/**
 * Clears all messages from the cached pages for a messages query key.
 * @param queryClient - TanStack Query client instance.
 * @param key - Query key for the messages infinite query.
 */
export const clearMessagesCache = (queryClient: QueryClient, key: readonly unknown[]) => {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(key, data => {
    if (!data?.pages?.length) {
      return data
    }

    const pages = data.pages.map(page => ({
      ...page,
      messages: [],
    }))

    return {
      ...data,
      pages,
    }
  })
}

/**
 * Removes the synthetic "typing" placeholder message from a message list.
 * @param messages - Current messages array.
 */
export const removeTypingMessages = (messages: IMessage[]) => {
  return messages.filter(message => message._id !== SpecialMessageId.TYPING)
}

/**
 * Prepends a synthetic "typing" placeholder if one does not already exist.
 * @param messages - Current messages array.
 * @param conversationId - Conversation for the typing placeholder.
 * @param sender - Sender for the typing placeholder.
 */
export const addTypingMessage = (messages: IMessage[], conversationId: string, sender: string) => {
  if (messages.some(message => message._id === SpecialMessageId.TYPING)) {
    return messages
  }

  return [
    {
      _id: SpecialMessageId.TYPING,
      sender,
      content: { signal: MessageSignal.TYPING },
      createdAt: new Date().toISOString(),
      conversationId,
    } as IMessage,
    ...messages,
  ]
}
